/* ShareWithDoctorCard — генериране на 7-дневен read-only token (iter420.5).
 *
 * UI: бутон "Сподели с лекар" → опционален label → copy-to-clipboard URL.
 * Списък с активни tokens с view count + revoke action.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Share2, Copy, CheckCircle2, Trash2, Eye, Loader2, Clock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { bg as bgLocale, enUS } from "date-fns/locale";

export default function ShareWithDoctorCard() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const locale = bg ? bgLocale : enUS;

  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/health-intelligence/share`);
      setTokens(data.items || []);
    } catch {
      setTokens([]);
    } finally { setLoading(false); }
  }, [API]);

  useEffect(() => { fetchTokens(); }, [fetchTokens]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/health-intelligence/share`,
                                         { label: label.trim() || null });
      toast.success(bg ? "Линкът е генериран ✓" : "Link generated ✓");
      setLabel("");
      // Auto-copy newly created share URL
      const url = `${window.location.origin}/share/health/${data.token}`;
      try { await navigator.clipboard.writeText(url); } catch {}
      setCopiedToken(data.token);
      setTimeout(() => setCopiedToken(null), 4000);
      fetchTokens();
    } catch (e) {
      toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Failed"));
    } finally { setCreating(false); }
  };

  const handleCopy = async (token) => {
    const url = `${window.location.origin}/share/health/${token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedToken(token);
      toast.success(bg ? "Копирано ✓" : "Copied ✓");
      setTimeout(() => setCopiedToken(null), 2500);
    } catch {
      toast.error(bg ? "Грешка при копиране" : "Copy failed");
    }
  };

  const handleRevoke = async (token) => {
    if (!window.confirm(bg ? "Revoke този линк? Лекарят повече няма да има достъп."
                            : "Revoke this link? The doctor will lose access.")) return;
    try {
      await axios.delete(`${API}/health-intelligence/share/${token}`);
      toast.success(bg ? "Revoked ✓" : "Revoked ✓");
      fetchTokens();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const activeTokens = tokens.filter((t) => t.is_active);

  return (
    <Card className={`border ${isDark ? "border-sky-500/30 bg-sky-500/5" : "border-sky-200 bg-sky-50/40"}`}
          data-testid="share-with-doctor-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="w-9 h-9 rounded-2xl flex items-center justify-center bg-gradient-to-br from-sky-500 to-cyan-500 text-white">
            <Share2 className="w-4.5 h-4.5" />
          </div>
          <div>
            <h3 className={`text-sm font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
              {bg ? "Сподели с лекар" : "Share with doctor"}
            </h3>
            <p className={`text-[11px] ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              {bg ? "Read-only линк валиден 7 дни — без login за лекаря."
                  : "Read-only link valid 7 days — no login needed for doctor."}
            </p>
          </div>
        </div>

        {/* Create new token */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={label} onChange={(e) => setLabel(e.target.value.slice(0, 120))}
                 placeholder={bg ? 'Опционално: "Д-р Иванов" (за твоя справка)' : 'Optional: "Dr. Smith" (for your reference)'}
                 className="text-xs" data-testid="share-label-input" />
          <Button onClick={handleCreate} disabled={creating}
                  className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white whitespace-nowrap"
                  data-testid="share-create-btn">
            {creating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Share2 className="w-3.5 h-3.5 mr-1.5" />}
            {bg ? "Генерирай линк" : "Generate link"}
          </Button>
        </div>

        {/* Active tokens list */}
        {loading ? (
          <div className="py-3 flex justify-center"><Loader2 className="w-4 h-4 animate-spin" /></div>
        ) : activeTokens.length === 0 ? (
          <p className={`text-[11px] text-center py-2 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
            {bg ? "Все още нямаш активни линкове." : "No active links yet."}
          </p>
        ) : (
          <div className="space-y-2">
            {activeTokens.map((t) => {
              const expiresIn = Math.round((new Date(t.expires_at) - new Date()) / (1000 * 60 * 60 * 24));
              return (
                <div key={t.token}
                     data-testid={`share-token-${t.token}`}
                     className={`p-2.5 rounded-lg border ${isDark ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white"}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <h4 className={`text-xs font-bold truncate ${isDark ? "text-white" : "text-slate-900"}`}>
                        {t.label || (bg ? "Без етикет" : "Unlabeled")}
                      </h4>
                      <div className="flex items-center gap-3 mt-1 text-[10.5px]">
                        <span className={isDark ? "text-slate-400" : "text-slate-500"}>
                          <Clock className="w-3 h-3 inline mr-0.5" />
                          {bg ? `Изтича след ${expiresIn} дни` : `Expires in ${expiresIn} days`}
                        </span>
                        {t.view_count > 0 && (
                          <span className={isDark ? "text-emerald-300" : "text-emerald-700"}>
                            <Eye className="w-3 h-3 inline mr-0.5" />
                            {t.view_count} {bg ? "гледан" : "views"}
                          </span>
                        )}
                      </div>
                      <code className={`block mt-1 text-[10px] truncate ${isDark ? "text-slate-500" : "text-slate-500"}`}>
                        /share/health/{t.token.slice(0, 16)}…
                      </code>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <Button onClick={() => handleCopy(t.token)} variant="outline" size="sm"
                              className="h-7 px-2 text-[10.5px]" data-testid={`share-copy-${t.token}`}>
                        {copiedToken === t.token
                          ? <><CheckCircle2 className="w-3 h-3 mr-1 text-emerald-500" /> {bg ? "Копирано" : "Copied"}</>
                          : <><Copy className="w-3 h-3 mr-1" /> {bg ? "Копирай" : "Copy"}</>}
                      </Button>
                      <Button onClick={() => handleRevoke(t.token)} variant="ghost" size="icon"
                              className="h-7 w-7" data-testid={`share-revoke-${t.token}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className={`text-[10px] leading-relaxed ${isDark ? "text-slate-500" : "text-slate-500"}`}>
          {bg
            ? "🔒 Лекарят вижда: conditions, симптоми, последни 5 хранения, последен лабораторен панел, VAIS, hi score, food recommendations. НЕ вижда: имейл, телефон, payment."
            : "🔒 Doctor sees: conditions, symptoms, last 5 meals, latest lab, VAIS, hi score, food recs. Doesn't see: email, phone, payment."}
        </p>
      </CardContent>
    </Card>
  );
}
